import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  User, Lock, Globe, Save, ArrowLeft, Phone, MapPin, 
  Building2, DollarSign, Mail, Award, Eye, EyeOff
} from 'lucide-react';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    full_name: '',
    phone: '',
    function: '',
    address: '',
    locality: '',
    country: '',
    language: 'ro'
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        phone: user.phone || '',
        function: user.function || '',
        address: user.address || '',
        locality: user.locality || '',
        country: user.country || 'Romania',
        language: user.language || 'ro'
      });
    }
  }, [user]);

  const handleProfileSave = async () => {
    try {
      setLoading(true);
      await api.put('/auth/profile', profileData);
      toast.success('Profil actualizat cu succes!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea profilului');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Parolele noi nu coincid');
      return;
    }
    if (passwordData.new_password.length < 5) {
      toast.error('Parola noua trebuie sa aiba minim 5 caractere');
      return;
    }

    try {
      setLoading(true);
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Parola schimbata cu succes!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la schimbarea parolei');
    } finally {
      setLoading(false);
    }
  };

  const languages = [
    { value: 'ro', label: 'Romana' },
    { value: 'en', label: 'English' },
    { value: 'de', label: 'Deutsch' },
    { value: 'fr', label: 'Francais' }
  ];

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="h-10 w-10 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Profilul Meu</h1>
            <p className="text-slate-600 mt-1">Gestioneaza datele personale si securitatea contului</p>
          </div>
        </div>
      </div>

      {/* User Summary Card */}
      <Card className="card-industrial bg-gradient-to-r from-slate-50 to-blue-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-[#0052CC] flex items-center justify-center text-white text-2xl font-bold">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-900">{user?.full_name}</h2>
              <p className="text-slate-600">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-[#0052CC] capitalize">{user?.role}</Badge>
                {user?.function && <Badge variant="outline">{user.function}</Badge>}
              </div>
            </div>
            {user?.hourly_cost && (
              <div className="text-right">
                <p className="text-sm text-slate-500">Cost ora</p>
                <p className="text-xl font-bold text-slate-900">{user.hourly_cost} LEI</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 h-12">
          <TabsTrigger value="profile" className="h-10" data-testid="tab-profile">
            <User className="w-4 h-4 mr-2" />
            Informatii Personale
          </TabsTrigger>
          <TabsTrigger value="security" className="h-10" data-testid="tab-security">
            <Lock className="w-4 h-4 mr-2" />
            Securitate
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-4">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg">Date Personale</CardTitle>
              <CardDescription>Actualizeaza informatiile tale de contact</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nume complet</Label>
                  <Input
                    value={profileData.full_name}
                    onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                    placeholder="Nume si prenume"
                    className="h-11"
                    data-testid="profile-fullname"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      placeholder="+40 7XX XXX XXX"
                      className="h-11 pl-10"
                      data-testid="profile-phone"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Functia</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={profileData.function}
                      onChange={(e) => setProfileData({ ...profileData, function: e.target.value })}
                      placeholder="Ex: Inginer mentenanta"
                      className="h-11 pl-10"
                      data-testid="profile-function"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Limba interfata</Label>
                  <Select
                    value={profileData.language}
                    onValueChange={(value) => setProfileData({ ...profileData, language: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="profile-language">
                      <Globe className="w-4 h-4 mr-2 text-slate-400" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((lang) => (
                        <SelectItem key={lang.value} value={lang.value}>
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {/* Address */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Adresa
                </h3>
                <div className="space-y-2">
                  <Label>Adresa completa</Label>
                  <Input
                    value={profileData.address}
                    onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                    placeholder="Strada, numar, bloc, apartament"
                    className="h-11"
                    data-testid="profile-address"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Localitate</Label>
                    <Input
                      value={profileData.locality}
                      onChange={(e) => setProfileData({ ...profileData, locality: e.target.value })}
                      placeholder="Oras / Comuna"
                      className="h-11"
                      data-testid="profile-locality"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tara</Label>
                    <Input
                      value={profileData.country}
                      onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                      placeholder="Romania"
                      className="h-11"
                      data-testid="profile-country"
                    />
                  </div>
                </div>
              </div>

              {/* Competencies (read-only) */}
              {user?.competency_names?.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="font-medium text-slate-900 flex items-center gap-2">
                      <Award className="w-4 h-4" />
                      Competente
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {user.competency_names.map((comp, idx) => (
                        <Badge key={idx} variant="outline" className="bg-blue-50">
                          {comp}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500">
                      Competentele sunt gestionate de administrator
                    </p>
                  </div>
                </>
              )}

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleProfileSave} 
                  disabled={loading}
                  className="btn-primary h-11 min-w-[150px]"
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'SE SALVEAZA...' : 'SALVEAZA'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-4">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg">Schimba Parola</CardTitle>
              <CardDescription>Actualizeaza parola contului tau</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="max-w-md space-y-4">
                <div className="space-y-2">
                  <Label>Parola actuala</Label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      placeholder="Introdu parola actuala"
                      className="h-11 pr-10"
                      data-testid="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Parola noua</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      placeholder="Minim 5 caractere"
                      className="h-11 pr-10"
                      data-testid="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Confirma parola noua</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Repeta parola noua"
                    className="h-11"
                    data-testid="confirm-password"
                  />
                  {passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                    <p className="text-xs text-red-500">Parolele nu coincid</p>
                  )}
                </div>
              </div>

              {/* Password requirements */}
              <div className="bg-slate-50 rounded-lg p-4 max-w-md">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Cerinte parola:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className={passwordData.new_password.length >= 5 ? 'text-green-600' : ''}>
                    • Minim 5 caractere
                  </li>
                  <li>• Recomandat: combinatie de litere, cifre si simboluri</li>
                </ul>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handlePasswordChange} 
                  disabled={loading || !passwordData.current_password || !passwordData.new_password || passwordData.new_password !== passwordData.confirm_password}
                  className="btn-primary h-11 min-w-[180px]"
                  data-testid="change-password-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {loading ? 'SE SCHIMBA...' : 'SCHIMBA PAROLA'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Email Info Card */}
          <Card className="card-industrial mt-4">
            <CardHeader>
              <CardTitle className="text-lg">Email</CardTitle>
              <CardDescription>Adresa de email asociata contului</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Mail className="w-5 h-5 text-slate-400" />
                <span className="font-medium">{user?.email}</span>
                <Badge variant="outline" className="ml-auto">Verificat</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Pentru a schimba adresa de email, contacteaza administratorul
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;
